'use client'

import { useGetHousingList, useGetSearchProfiles } from "@/queris";
import { Category, Equipment, OfferType, Unit } from "@/types";
import { useTranslations } from "next-intl";
import { FC, useEffect, useMemo, useState } from "react";
import { Container, List } from "./styled";
import { Typography } from "@mui/material";
import { COLORS } from "@/constants";
import { CustomPagination, NoData } from "@/components";
import { SearchProfileCard } from "./components";
import { SearchProfileSkeleton } from "./SearchProfileSkeleton";

export type BaseFields = {
  categories: Category[] | null
  equipment: Equipment[] | null
  districts: Unit[] | null,
  countries: Unit[] | null,
  division: Unit[] | null,
  powerConsumptionClasses: Category[] | null,
  energyClassFactors: Category[] | null,
}

const PAGE_SIZE = 10;

export const SearchProfileScreen: FC = () => {
  const t = useTranslations()

  const { data, isLoading, isPending } = useGetSearchProfiles()
  const { data: searchData } = useGetHousingList({ offerType: OfferType.SELL });

  const [page, setPage] = useState(0)

  const [fieldBase, setFieldBase] = useState<BaseFields>({
    categories: null,
    equipment: null,
    districts: null,
    countries: null,
    division: null,
    powerConsumptionClasses: null,
    energyClassFactors: null,
  })

  useEffect(() => {
    if (!searchData?.objects) return;

    const { filterData } = searchData;

    queueMicrotask(() => {
      setFieldBase({
        categories: filterData.categories ?? null,
        equipment: filterData.equipment ?? null,
        countries: filterData.countries ?? null,
        districts: filterData.districts ?? null,
        division: filterData.federalStates ?? null,
        powerConsumptionClasses: filterData.powerConsumptionClasses
          ? filterData.powerConsumptionClasses
              .filter(item => item.code && item.code.toLowerCase() !== 'null')
              .map(item => ({ ...item, title: item.title || item.code }))
          : null,
        energyClassFactors: filterData.energyClassFactors
          ? filterData.energyClassFactors
              .filter(item => item.code && item.code.toLowerCase() !== 'null')
              .map(item => ({ ...item, title: item.title || item.code }))
          : null,
      });
    });
  }, [searchData]);

  const allProfiles = useMemo(
    () => [...(data ?? [])].sort((a, b) => b.id - a.id),
    [data],
  );

  const paginatedProfiles = useMemo(
    () => allProfiles.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE),
    [allProfiles, page],
  );

  const isLoad = isLoading || isPending

  return (
    <Container>
      <Typography
        color={COLORS['primary/900']} fontSize={30} fontWeight={700} lineHeight={1}>{t('navigation.search-profile')}
      </Typography>
      {isLoad ? (
        <SearchProfileSkeleton />
      ) : allProfiles.length > 0 ? (
        <>
          <List>
            {paginatedProfiles.map((e) => (
              <SearchProfileCard
                key={e.id}
                data={e}
                fieldBase={fieldBase}
              />
            ))}
          </List>
          <CustomPagination
            page={page}
            numFound={allProfiles.length}
            itemsPerPage={PAGE_SIZE}
            onPageChange={setPage}
          />
        </>
      ) : (
        <NoData message={t('searchProfile.noData')} />
      )}
    </Container>
  )
}
